/**
 * Recursively renders a page tree using the shared component registry.
 *
 * Dependency-light, read-only renderer:
 *  - props are validated via `entry.schema.safeParse(...)`; invalid nodes
 *    render a labeled error block (so you see which node failed and why).
 *  - selection is OPTIONAL: pass `selectedNodeId` + `onSelect` to get a
 *    dashed outline + corner label and click-to-select. Omit both and the
 *    tree renders as a plain, unobtrusive page preview.
 */

import { useMemo } from "react";
import type { ComponentType, CSSProperties, ReactNode } from "react";
import clsx from "clsx";

import { registry, type RegistryName } from "@dynamic-landing/components";
import {
  BLOCK_WIDTH_MAXW,
  blockAlignKey,
  blockWidthKey,
  COL_SPAN_VALUES,
  LAYOUT_PARENT_NAMES,
  ROW_SPAN_VALUES,
} from "@dynamic-landing/layout";

import type { SchemaLike } from "./schemaIntrospect";
import type { ComponentNode } from "./types";

type RegistryEntry = (typeof registry)[RegistryName];

/**
 * Components whose direct children participate in a CSS grid. When the PARENT
 * is one of these, the renderer reads `colSpan`/`rowSpan` off each child node's
 * props and applies grid placement to the child's WRAPPER — the child
 * component itself stays span-unaware (and these keys are intentionally absent
 * from every component schema; zod strips them on parse).
 */
const LAYOUT_PARENTS = new Set<string>(LAYOUT_PARENT_NAMES);

// Curated span enums (single source: @dynamic-landing/layout). Guarded so a
// hand-authored / AI / recipe tree can never inject arbitrary CSS here.
const COL_SPANS = new Set<string>(COL_SPAN_VALUES);
const ROW_SPANS = new Set<string>(ROW_SPAN_VALUES);

/** Build grid placement style from a child node's props.colSpan / props.rowSpan. */
function layoutSpanStyle(props: Record<string, unknown>): CSSProperties {
  const style: CSSProperties = {};
  const col = props.colSpan;
  const row = props.rowSpan;
  const colKey = typeof col === "number" ? String(col) : typeof col === "string" ? col : null;
  const rowKey = typeof row === "number" ? String(row) : typeof row === "string" ? row : null;
  if (colKey && COL_SPANS.has(colKey)) style.gridColumn = `span ${colKey} / span ${colKey}`;
  if (rowKey && ROW_SPANS.has(rowKey)) style.gridRow = `span ${rowKey} / span ${rowKey}`;
  return style;
}

/** Build width/alignment style from a node's generic props.blockWidth/blockAlign.
 *  Applied to ANY block's wrapper (not just grid children); "full"/unset = none. */
function blockSizeStyle(props: Record<string, unknown>): CSSProperties {
  const style: CSSProperties = {};
  const w = blockWidthKey(props.blockWidth);
  if (w && w !== "full") {
    style.maxWidth = BLOCK_WIDTH_MAXW[w];
    style.width = "100%";
    // Alignment only matters once a width constrains the block.
    const a = blockAlignKey(props.blockAlign);
    if (a === "center") {
      style.marginLeft = "auto";
      style.marginRight = "auto";
    } else if (a === "right") {
      style.marginLeft = "auto";
    } else {
      style.marginRight = "auto";
    }
  }
  return style;
}

/** Sizing props that can carry a per-breakpoint override (#22). */
export type Breakpoint = "base" | "mobile";
const RESPONSIVE_SIZING_KEYS = ["blockWidth", "blockAlign", "colSpan", "rowSpan"] as const;

/** Merge a node's base sizing props with its `responsive[breakpoint]` overrides.
 *  Overrides live in `props.responsive` (a plain object persisted in the tree),
 *  so they survive save/publish and a migrated renderer can honor them too. */
export function mergeResponsiveSizing(
  props: Record<string, unknown>,
  breakpoint: Breakpoint,
): Record<string, unknown> {
  if (breakpoint === "base") return props;
  const resp = props.responsive as Record<string, Record<string, unknown>> | undefined;
  const ov = resp?.[breakpoint];
  if (!ov) return props;
  const merged: Record<string, unknown> = { ...props };
  for (const k of RESPONSIVE_SIZING_KEYS) {
    if (k in ov) merged[k] = ov[k];
  }
  return merged;
}

/** Compose a child's wrapper style: grid placement (in a layout parent) merged
 *  with generic block sizing. Returns undefined when there's nothing to apply. */
function childWrapperStyle(props: Record<string, unknown>, inLayoutParent: boolean): CSSProperties | undefined {
  const merged: CSSProperties = {
    ...(inLayoutParent ? layoutSpanStyle(props) : {}),
    ...blockSizeStyle(props),
  };
  return Object.keys(merged).length ? merged : undefined;
}

/** Wrap an element in a span-carrying div, but only when there is a span to apply. */
function maybeWrapSpan(el: ReactNode, style: CSSProperties | undefined): ReactNode {
  if (!style || Object.keys(style).length === 0) return el;
  return <div style={style}>{el}</div>;
}

export type TreeRendererProps = {
  node: ComponentNode;
  /** Currently-selected node id, or null. Omit for a plain preview. */
  selectedNodeId?: string | null;
  /** Selection callback. Omit to disable selection entirely. */
  onSelect?: (nodeId: string) => void;
  /**
   * Grid placement applied to this node's wrapper. Set by the parent NodeView
   * when the parent is a layout container (Grid/Columns); never set for root.
   */
  wrapperStyle?: CSSProperties;
  /** Active breakpoint for responsive sizing resolution (#22). Editor passes
   *  the device toggle ("mobile") so the preview reflects per-breakpoint props;
   *  defaults to "base" (desktop). */
  breakpoint?: Breakpoint;
};

export function TreeRenderer(props: TreeRendererProps) {
  return <NodeView {...props} />;
}

function NodeView({ node, selectedNodeId = null, onSelect, wrapperStyle, breakpoint = "base" }: TreeRendererProps) {
  const entry = useMemo<RegistryEntry | undefined>(() => {
    return (registry as Record<string, RegistryEntry | undefined>)[node.component.name];
  }, [node.component.name]);

  const selectable = typeof onSelect === "function";
  const isSelected = selectedNodeId === node.node_id;

  if (!entry) {
    return (
      <UnknownComponent
        node={node}
        message={`Unknown component "${node.component.name}". Not in registry.`}
        selected={isSelected}
        onSelect={onSelect}
        wrapperStyle={wrapperStyle}
      />
    );
  }

  const schema = entry.schema as unknown as SchemaLike;
  const parse = schema.safeParse(node.props);
  if (!parse.success) {
    return (
      <UnknownComponent
        node={node}
        message={`Invalid props for ${node.component.name}: ${parse.error.message}`}
        selected={isSelected}
        onSelect={onSelect}
        wrapperStyle={wrapperStyle}
      />
    );
  }

  // Render the registry component with parsed props + node_id pass-through
  // so analytics can later attribute clicks to nodes.
  const Component = entry.component as ComponentType<
    Record<string, unknown> & { children?: ReactNode }
  >;

  // When THIS node is a layout container, each child's wrapper gets grid
  // placement derived from that child's own props.colSpan / props.rowSpan.
  const isLayoutParent = LAYOUT_PARENTS.has(node.component.name);
  const childrenEls = entry.acceptsChildren
    ? (node.children ?? []).map((c) => (
        <NodeView
          key={c.node_id}
          node={c}
          selectedNodeId={selectedNodeId}
          onSelect={onSelect}
          breakpoint={breakpoint}
          wrapperStyle={childWrapperStyle(mergeResponsiveSizing(c.props, breakpoint), isLayoutParent)}
        />
      ))
    : null;

  const rendered = (
    <Component {...(parse.data as Record<string, unknown>)} data-node-id={node.node_id}>
      {childrenEls}
    </Component>
  );

  if (!selectable) return maybeWrapSpan(rendered, wrapperStyle);

  return (
    <SelectionWrapper
      nodeId={node.node_id}
      label={node.component.name}
      selected={isSelected}
      onSelect={onSelect}
      style={wrapperStyle}
    >
      {rendered}
    </SelectionWrapper>
  );
}

function SelectionWrapper({
  nodeId,
  label,
  selected,
  onSelect,
  children,
  style,
}: {
  nodeId: string;
  label: string;
  selected: boolean;
  onSelect: ((id: string) => void) | undefined;
  children: ReactNode;
  /** Grid placement (colSpan/rowSpan) when the parent is a layout container. */
  style?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={clsx(
        "relative group cursor-pointer",
        selected &&
          "outline outline-2 outline-dashed outline-accent outline-offset-2 rounded",
      )}
      onClick={(e) => {
        // When clicking inline-editable text of the ALREADY-selected node, let
        // the browser place the caret instead of hijacking for (re)selection.
        const target = e.target as HTMLElement;
        if (selected && target.closest?.("[data-dl-edit]")) return;
        // Bubble phase: deepest wrapper handler runs first, so the most
        // specific node wins. preventDefault stops <a> navigation in CTAs
        // when clicking inside the editor preview.
        e.stopPropagation();
        e.preventDefault();
        onSelect?.(nodeId);
      }}
    >
      <div
        className={clsx(
          "absolute -top-3 left-0 z-10 px-2 py-0.5 rounded-tl rounded-tr text-[10px] uppercase tracking-wide text-white font-medium",
          selected
            ? "bg-accent"
            : "bg-neutral-900 opacity-0 group-hover:opacity-100 transition pointer-events-none",
        )}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function UnknownComponent({
  node,
  message,
  selected,
  onSelect,
  wrapperStyle,
}: {
  node: ComponentNode;
  message: string;
  selected: boolean;
  onSelect: ((id: string) => void) | undefined;
  wrapperStyle?: CSSProperties;
}) {
  const block = (
    <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
      <p className="font-medium">Render error in node {node.node_id}</p>
      <p className="text-xs mt-1 font-mono">{message}</p>
    </div>
  );
  if (typeof onSelect !== "function") return maybeWrapSpan(block, wrapperStyle);
  return (
    <SelectionWrapper
      nodeId={node.node_id}
      label={`! ${node.component.name}`}
      selected={selected}
      onSelect={onSelect}
      style={wrapperStyle}
    >
      {block}
    </SelectionWrapper>
  );
}
